import {
  Image,
  List,
  ListDisplayType,
  Page,
  Tile as TileModel,
  Video,
  ImageKind,
  getImageByOrientation,
} from "@blacknut/javascript-sdk/dist";
import { logE } from "@blacknut/logging/dist";
import { Focusable, useSpatialNavigation } from "@blacknut/spatialnav-sdk/dist";
import clsx from "clsx";
import { AppLayout, State, StorageKey, useLayout, useTile } from "core";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { useSelector } from "react-redux";
import { ReactComponent as IcPassOverlay } from "../../assets/dist/ic_pass_overlay.svg";
import { useProfiler } from "../../hooks/useProfiler";
import { useTileSize } from "../../utils/TileWidthProvider";
import { LOGGING_TAG } from "../../utils/Utils";
import FadeInImage from "../FadeInImage/FadeInImage";
import { usePreview } from "../Modals/PreviewContext";
import styles from "./styles.module.scss";
import { useOrientation } from "src/utils/OrientationContext";
import { useScrollContext } from "src/utils/scroll/ScrollProvider";

interface TileProps {
  className?: string;
  item: TileModel;
  featured?: boolean;
  list: List;
  decorate?: (t: TileModel, focused: boolean) => React.ReactNode;
  onClick?: () => void;
  page?: Page;
  index?: number;
}

const Tile = ({
  className,
  item,
  featured = false,
  list,
  page,
  decorate,
  onClick: onClickFromProps,
  index = 0,
}: TileProps) => {
  const list16_9 = list.display === ListDisplayType.FEATURED;
  const { orientation } = useOrientation();
  const { active: previewActive } = usePreview();
  const { onClick } = useTile({
    item,
    previewMode: previewActive,
    pageUuid: page?.uuid,
  });
  const { update: updateScrollContext } = useScrollContext();
  const { config } = useSelector((state: State) => state.globalState);
  let image: Image | undefined;
  let videoThumbnail: Video | undefined;
  const videoItem = useRef<HTMLVideoElement | null>(null);
  if (item.game) {
    let mainImages: Image[] | undefined;
    if (list.display === ListDisplayType.BANNER) {
      mainImages = item.game.images?.filter((im) => im.kind === ImageKind.BANNER);
    } else if (list16_9) {
      mainImages = item.game.images?.filter((im) => im.kind === ImageKind.GAME_HERO);
    } else {
      mainImages = item.game.images?.filter((im) => im.kind === ImageKind.THUMBNAIL);
    }

    image = mainImages ? mainImages[0] : undefined;
    const videoThumbnails =
      item.game &&
      item.game.videos &&
      item.game.videos.filter((v) => v.type === "thumbnail");
    videoThumbnail = videoThumbnails ? videoThumbnails[0] : undefined;
  } else if (item.link) {
    const themedImg = getImageByOrientation(orientation, item.link.images || []);
    image = themedImg || undefined;
  } else if (item.page) {
    const images = item.page[list16_9 ? "imgRectangular" : "imgSquare"] as Image[];
    image = getImageByOrientation(orientation, images || []);
  } else {
    logE(LOGGING_TAG, "invalid tile: %o", item);
  }
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hovering, setHovering] = useState(false);
  const { active: spatialNavigationActive } = useSpatialNavigation();
  const isTVLegacy = localStorage.getItem(StorageKey.TV_LEGACY) === "true";
  const onTimeUpdate = useCallback(() => {
    if (videoItem && videoItem.current && videoItem.current.currentTime > 0.25) {
      setVideoPlaying(true);
    }
  }, []);

  const isCategorySVGTile = item.page && image?.url.endsWith("svg");

  const onMouseEnter = useCallback(() => {
    setHovering(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    unstable_batchedUpdates(() => {
      setFocused(false);
      setHovering(false);
    });
  }, []);

  const onBlur = useCallback(() => {
    unstable_batchedUpdates(() => {
      setFocused(false);
    });
  }, []);

  const onFocus = useCallback(
    (e) => {
      const selector = "." + [...e.srcElement.classList].join(".");
      updateScrollContext(selector);
      setFocused(true);
    },
    [updateScrollContext],
  );

  useEffect(() => {
    if (spatialNavigationActive && hovering) {
      setHovering(false);
    }
  }, [hovering, spatialNavigationActive]);

  const layout = useLayout();
  const isWaitingTimeout: { current: NodeJS.Timeout | null } = useRef(null);
  const [longFocused, setLongFocused] = useState(false);
  useEffect(() => {
    if (layout !== AppLayout.TV) return;
    if (!focused) {
      setLongFocused(false);
      clearTimeout(isWaitingTimeout.current as NodeJS.Timeout);
    } else {
      isWaitingTimeout.current = setTimeout(() => {
        setLongFocused(true);
      }, 1000);
    }

    return () => {
      clearTimeout(isWaitingTimeout.current as NodeJS.Timeout);
    };
  }, [focused, layout]);

  const { standardTileSize, featuredTileSize, featured16By9TileSize } = useTileSize();

  const Root = useProfiler("tile");
  const blacknutPlus = !!config?.features?.blacknutplus;
  const isPassOnly =
    item.game?.addonOption?.inGamePass && !item.game?.addonOption?.isBonusGame;
  const isUnlocked =
    item.game?.addonOption?.userHasGamePass || item.game?.addonOption?.userHasBonusGame;

  return (
    <Root>
      <Focusable
        onPressEnter={onClickFromProps || onClick}
        className={clsx([
          styles.container,
          // !WARNING! DO NOT use class "tile" for styling
          // It is purposely not scoped since it needs to be accessible from FocusableSection defaultElement prop
          "tile",
          styles.focusable,
          spatialNavigationActive && styles.spatialNavigationActive,
          isCategorySVGTile && styles.categorySVG,
          `tile_${index}`, // Combined with the "path" to have a unique selector to be focused
          className,
        ])}
        onMouseEnter={layout === AppLayout.DESKTOP ? onMouseEnter : undefined}
        onMouseLeave={layout === AppLayout.DESKTOP ? onMouseLeave : undefined}
        onClick={onClickFromProps || onClick}
        data-testid="tile"
        style={{
          width:
            list.display === ListDisplayType.FEATURED
              ? featured16By9TileSize.width
              : featured
              ? featuredTileSize.width
              : standardTileSize.width,
          height:
            list.display === ListDisplayType.FEATURED
              ? featured16By9TileSize.height
              : featured
              ? featuredTileSize.height
              : standardTileSize.height,
        }}
        onFocus={onFocus}
        onMouseDown={(e) => e.preventDefault()}
        onBlur={onBlur}
      >
        <div className={clsx([styles.wrap])}>
          <div className={styles.imageContainer}>
            <FadeInImage
              image={image}
              suffixOnPixelRatio1={featured ? "_md" : "_sm"}
              className={clsx([
                isCategorySVGTile && styles.categorySVGImage,
                list16_9 && styles.list16_9,
              ])}
            />
          </div>

          {item.page?.title && list.displayTilesTitle && (
            <h3 className={styles.pageTitle}>{item.page.title}</h3>
          )}

          {!decorate && (
            <>
              {!isTVLegacy &&
                !list16_9 &&
                (layout !== AppLayout.TV || longFocused) &&
                videoThumbnail &&
                ((hovering && !spatialNavigationActive) ||
                  (spatialNavigationActive && focused)) && (
                  <video
                    ref={videoItem}
                    data-testid="video"
                    className={clsx([styles.video, videoPlaying && styles.playing])}
                    muted
                    autoPlay
                    playsInline
                    onTimeUpdate={videoPlaying ? undefined : onTimeUpdate}
                    src={videoThumbnail?.url}
                    loop={true}
                  />
                )}
            </>
          )}

          {blacknutPlus && isPassOnly && !isUnlocked && (
            <span
              className={clsx(
                styles.bonus,
                layout === AppLayout.PHONE && styles.onMobileBonus,
              )}
              data-testid="bonus"
            >
              <IcPassOverlay />
            </span>
          )}

          {decorate && decorate(item, false)}
        </div>
      </Focusable>
    </Root>
  );
};
export default React.memo(Tile);
